import { useLayoutEffect, useRef, type CSSProperties, type KeyboardEvent, type ReactNode, type Ref } from "react";
import { cx } from "../cx";

/**
 * Tabs de línea del sistema. Controladas por la página, con el teclado del
 * patrón de tablist (como la canónica de simulador.html y `AccountTabs`):
 * flechas izquierda/derecha (e Inicio/Fin) mueven el foco Y abren la
 * pestaña, y solo la abierta está en el orden de tabulación (tabindex
 * itinerante). `label` nombra el grupo para el lector.
 */
export function Tabs({ children, style, className, label }: {
  children: ReactNode; style?: CSSProperties | undefined; className?: string | undefined; label?: string | undefined;
}) {
  const caja = useRef<HTMLDivElement>(null);
  // si ninguna está abierta (la abierta desapareció), la primera queda alcanzable con Tab
  useLayoutEffect(() => {
    const ts = [...(caja.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [])];
    const abierta = ts.find((t) => t.getAttribute("aria-selected") === "true") ?? ts[0];
    for (const t of ts) t.tabIndex = t === abierta ? 0 : -1;
  });
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End") return;
    const ts = [...(caja.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])];
    const i = ts.indexOf(e.target as HTMLButtonElement);
    if (i < 0) return;
    e.preventDefault();
    const n = e.key === "Home" ? 0 : e.key === "End" ? ts.length - 1 : (i + (e.key === "ArrowRight" ? 1 : ts.length - 1)) % ts.length;
    const t = ts[n];
    if (t === undefined) return;
    t.focus();
    t.click();
  };
  return <div ref={caja} className={cx("sk-tabs", className)} role="tablist" aria-label={label} style={style} onKeyDown={key}>{children}</div>;
}
/** Una pestaña. `controls` = id de su panel (`TabPanel`); `id` lo usa el panel para titularse. */
export function Tab({ selected, mod, onClick, children, id, controls, ref }: {
  selected: boolean; mod?: ReactNode | undefined; onClick: () => void; children: ReactNode;
  id?: string | undefined; controls?: string | undefined; ref?: Ref<HTMLButtonElement> | undefined;
}) {
  return (
    <button ref={ref} type="button" id={id} className="sk-tab" role="tab" aria-selected={selected} aria-controls={controls} tabIndex={selected ? 0 : -1} onClick={onClick}>
      {children}{mod !== undefined && <span className="sk-mod">{mod}</span>}
    </button>
  );
}

const ENFOCABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * El panel de una pestaña (patrón APG, como la canónica):
 * - es enfocable (`tabindex=0`) SOLO si no tiene nada enfocable dentro; si
 *   lo tiene, el Tab ya llega a su contenido;
 * - si el foco estaba dentro y lo que lo tenía desaparece al repintar (un
 *   enlace del banner que lanza otra ejecución, un «reintentar»), el foco
 *   vuelve a la pestaña ABIERTA de su tablist en vez de caer a `<body>`.
 * Sin estilo propio: los paneles ocultos llevan `hidden`.
 */
export function TabPanel({ id, labelledBy, hidden, className, children }: {
  id: string; labelledBy: string; hidden?: boolean | undefined; className?: string | undefined; children?: ReactNode;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const conFoco = useRef<Element | null>(null);
  useLayoutEffect(() => {
    const p = caja.current;
    if (p === null) return;
    if (p.querySelector(ENFOCABLE) !== null) p.removeAttribute("tabindex"); else p.setAttribute("tabindex", "0");
    const f = conFoco.current;
    if (f === null || f.isConnected) return;
    conFoco.current = null;
    const a = document.activeElement;
    if (a !== null && a !== document.body) return;
    const lista = document.getElementById(labelledBy)?.closest('[role="tablist"]');
    (lista?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]') ?? document.getElementById(labelledBy))?.focus();
  });
  return (
    <div
      ref={caja} id={id} role="tabpanel" aria-labelledby={labelledBy} hidden={hidden} className={className}
      onFocus={(e) => { conFoco.current = e.target; }}
      onBlur={(e) => {
        const a = e.relatedTarget as Node | null;
        if (a !== null) { if (!e.currentTarget.contains(a)) conFoco.current = null; return; }
        // sin destino: o se fue a ninguna parte (sigue en el DOM) o lo quitó el repintado
        const f = e.target;
        setTimeout(() => { if (f.isConnected && conFoco.current === f) conFoco.current = null; }, 0);
      }}
    >
      {children}
    </div>
  );
}

/**
 * La píldora como ELECCIÓN, no como pestaña (inventario 2026-09-15): un
 * grupo de botones con `aria-pressed` cuando NO hay panel que controlar —
 * el «¿cómo fue?» del acta de una decisión, el formato de los números. No
 * es `role=tablist`: un tablist promete paneles, y aquí no los hay.
 */
export function PillGroup({ label, describedBy, children, style }: {
  label: string; describedBy?: string | undefined; children: ReactNode; style?: CSSProperties | undefined;
}) {
  return <div className="sk-tabs sk-tabs--pill" role="group" aria-label={label} aria-describedby={describedBy} style={style}>{children}</div>;
}
/**
 * Una elección del grupo. `stale` la apaga CON PORQUÉ (`aria-disabled` +
 * tooltip, fuera del teclado): sigue a la vista y el motivo se puede leer,
 * al revés que `[disabled]`, que deja al usuario sin respuesta.
 */
export function Pill({ pressed, onClick, stale, tip, tipPos, children }: {
  pressed: boolean; onClick: () => void; stale?: boolean | undefined;
  tip?: string | undefined; tipPos?: "bottom" | "right" | "left" | undefined; children: ReactNode;
}) {
  return (
    <button
      type="button" className="sk-tab" aria-pressed={pressed}
      data-sk-tip={tip} data-sk-tip-pos={tip === undefined ? undefined : tipPos}
      {...(stale === true && { "aria-disabled": true as const, tabIndex: -1 })}
      onClick={() => { if (stale !== true) onClick(); }}
    >
      {children}
    </button>
  );
}
